// One clock. A `Clock` is a snapshot of the transport: at AudioContext time `ctx` the sound leaving the
// stretch node is at song position `song`, moving at `rate` song seconds per context second, inside an
// optional loop. Every time the engine needs a time it asks here: the count display (`heardSong`), the
// cue scheduler (`playSpans`, `songToCtx`) and the tap reader (`heardSong`). Nothing else converts between
// song time and context time.
//
// Times: `song` is seconds in the file (or in one lap of the Rhythm Lab); `ctx` is AudioContext.currentTime.
// The stretch node compensates its own latency (measured, docs/design/spikes.md), so the map holds at the
// node's output; the speaker lags that by `outputLatency` (heard = node output + outputLatency), and the
// node leaves a measured residual (`latency`).
//
// A loop wraps the way the stretcher's loop does: once the position reaches `loop.end` it moves back by the
// loop's length. A position before `loop.start` plays on normally until it reaches `loop.end` (a loop switched
// on ahead of the playhead).

export interface Loop {
  /** Song seconds, inclusive. */
  readonly start: number;
  /** Song seconds, exclusive. Greater than `start`. */
  readonly end: number;
}

export interface Clock {
  /** Context time of the anchor. */
  readonly ctx: number;
  /** Song position sounding at the node's output at `ctx`, in seconds. */
  readonly song: number;
  /** Song seconds per context second (0.5 to 1 in the app). */
  readonly rate: number;
  readonly loop: Loop | null;
  /** false: paused; the song position stays at `song`. */
  readonly playing: boolean;
  /** Seconds the node's sound lags its own time map: the residual left after its latency compensation. */
  readonly latency: number;
  /**
   * The clock this one replaced. The speaker is `outputLatency` behind the node, so for that long after a change the listener still
   * hears the old clock's sound; `heardSong` follows it until the new anchor is heard. The transport keeps a few seconds of history.
   */
  readonly previous?: Clock | undefined;
}

/** The stretch node's residual lag, measured offline in Chromium (docs/design/spikes.md): about 1.3 ms at 50 to 100% speed. */
export const STRETCH_RESIDUAL_S = 0.0013;

const mod = (a: number, n: number): number => ((a % n) + n) % n;

/** Position `s` (not wrapped) as the loop would play it. */
export function wrapIntoLoop(s: number, loop: Loop | null): number {
  return loop && s >= loop.end ? loop.start + mod(s - loop.start, loop.end - loop.start) : s;
}

/** Song position sounding at the node's output at context time t. Before the anchor it extrapolates backwards. */
export function songAt(c: Clock, t: number): number {
  if (!c.playing) return c.song;
  return wrapIntoLoop(c.song + (t - c.latency - c.ctx) * c.rate, c.loop);
}

/**
 * Song position the listener hears at context time `now`: the node's output `outputLatency` ago (the speaker's lag, applied once).
 * Until a new clock's first sound has reached the listener they still hear the clock before it (a speed or loop change made while
 * playing, a seek, a pause); with no earlier sound (it was paused) the position is held at the anchor.
 */
export function heardSong(c: Clock, now: number, outputLatency: number): number {
  return soundAt(c, now - outputLatency);
}

/** The clock whose sound left the node at context time t (`c`, or one of its predecessors). */
export function clockAt(c: Clock, t: number): Clock {
  const first = c.ctx + c.latency; // the first context time this clock's own sound leaves the node
  return t < first && c.previous?.playing ? clockAt(c.previous, t) : c; // that sound is still on its way (a paused clock has none)
}

/** Song position of the sound that left the node at context time t, following the clock that was in force then. */
function soundAt(c: Clock, t: number): number {
  const k = clockAt(c, t);
  return !k.playing || t < k.ctx + k.latency ? k.song : songAt(k, t);
}

/** `c` with its history cut to the last `keepS` seconds and `depth` clocks: a heard position never looks further back than the speaker's lag. */
export function trimHistory(c: Clock, now: number, keepS = 2, depth = 8): Clock {
  if (!c.previous) return c;
  if (depth <= 0 || c.ctx < now - keepS) return { ...c, previous: undefined };
  return { ...c, previous: trimHistory(c.previous, now, keepS, depth - 1) };
}

/** Times the loop has wrapped between the anchor and context time t (0 with no loop). */
export function lapsAt(c: Clock, t: number): number {
  if (!c.playing || !c.loop) return 0;
  const s = c.song + (t - c.latency - c.ctx) * c.rate;
  return s < c.loop.end ? 0 : 1 + Math.floor((s - c.loop.end) / (c.loop.end - c.loop.start));
}

/**
 * Context time at which song position `song` is sounding at the node's output, the earliest one at or after
 * `notBefore` (default the anchor). In a loop a position inside it comes round again every lap. Null if
 * paused, or if the position never sounds (before the playhead with no loop, or outside the loop and behind it).
 */
export function songToCtx(c: Clock, song: number, notBefore: number = c.ctx): number | null {
  if (!c.playing) return null;
  const { loop, rate } = c;
  const origin = c.ctx + c.latency;
  let first: number; // first time the position sounds
  let repeats = false;
  if (!loop) {
    if (song < c.song) return null;
    first = origin + (song - c.song) / rate;
  } else if (song >= c.song && song < loop.end) {
    first = origin + (song - c.song) / rate;
    repeats = song >= loop.start;
  } else if (song >= loop.start && song < loop.end) {
    first = origin + (loop.end - c.song + (song - loop.start)) / rate;
    repeats = true;
  } else {
    return null;
  }
  if (first >= notBefore) return first;
  if (!repeats || !loop) return null;
  const lap = (loop.end - loop.start) / rate;
  return first + Math.ceil((notBefore - first) / lap) * lap;
}

/** A stretch of the clock with no wrap in it: song [song0, song1) sounds from context time ctx0 at `rate`. */
export interface Span {
  readonly song0: number;
  readonly song1: number;
  readonly ctx0: number;
  readonly rate: number;
}

/**
 * The song ranges that sound in context time [from, to), in order, split where the loop wraps (a span
 * never contains a wrap, so a span's cues can be found with one call to `cuesBetween`). Empty when paused
 * or when the window ends before the anchor sounds. Consecutive windows give consecutive, non-overlapping spans.
 */
export function playSpans(c: Clock, from: number, to: number): Span[] {
  if (!c.playing || !(to > from)) return [];
  const { loop, rate } = c;
  const spans: Span[] = [];
  let t = Math.max(from, c.ctx + c.latency);
  let s = wrapIntoLoop(c.song + (t - c.latency - c.ctx) * rate, loop);
  for (let guard = 0; t < to && guard < 64; guard++) {
    const room = loop ? (loop.end - s) / rate : Infinity;
    const dt = Math.min(to - t, room);
    if (!(dt > 0)) break;
    const wraps = loop !== null && dt >= room;
    spans.push({ song0: s, song1: wraps ? loop.end : s + dt * rate, ctx0: t, rate });
    t += dt;
    s = wraps ? loop.start : s + dt * rate;
  }
  return spans;
}
